<template>
    <div class="hris-profile w-full max-w-sm">
        <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 body-small w-full">
            <!-- Editable name -->
            <dt class="font-medium text-valence-grey-750">Your name</dt>
            <dd>
                <div
                    v-if="!editingName"
                    role="button"
                    tabindex="0"
                    class="group flex items-center gap-2 cursor-pointer text-gray-600"
                    @click="startEditingName"
                    @keydown.enter="startEditingName">
                    {{ name || "—" }}
                    <i class="bi bi-pencil text-xs text-valence-grey-400 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true"></i>
                </div>
                <input
                    v-else
                    ref="nameInputRef"
                    :value="name"
                    aria-label="Name"
                    type="text"
                    class="inline-edit-input"
                    @input="$emit('update:name', $event.target.value)"
                    @blur="editingName = false"
                    @keydown.enter="editingName = false" />
            </dd>

            <!-- Editable role -->
            <dt class="font-medium text-valence-grey-750">Your role</dt>
            <dd>
                <div
                    v-if="!editingRole"
                    role="button"
                    tabindex="0"
                    class="group flex items-center gap-2 cursor-pointer text-gray-600"
                    @click="startEditingRole"
                    @keydown.enter="startEditingRole">
                    {{ role || "—" }}
                    <i class="bi bi-pencil text-xs text-valence-grey-400 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true"></i>
                </div>
                <input
                    v-else
                    ref="roleInputRef"
                    :value="role"
                    aria-label="Role"
                    type="text"
                    class="inline-edit-input"
                    @input="$emit('update:role', $event.target.value)"
                    @blur="editingRole = false"
                    @keydown.enter="editingRole = false" />
            </dd>

            <!-- Read-only HRIS fields -->
            <template v-for="field in readOnlyFields" :key="field.key">
                <dt class="font-medium text-valence-grey-750">{{ field.label }}</dt>
                <dd class="text-gray-600">{{ field.value }}</dd>
            </template>
        </dl>
    </div>
</template>

<script setup>
import { computed, nextTick, ref } from "vue";

const props = defineProps({
    name: { type: String, default: "" },
    role: { type: String, default: "" },
    hrisData: { type: Object, required: true },
});

defineEmits(["update:name", "update:role"]);

const editingName = ref(false);
const editingRole = ref(false);
const nameInputRef = ref(null);
const roleInputRef = ref(null);

function startEditingName() {
    editingName.value = true;
    nextTick(() => nameInputRef.value?.focus());
}

function startEditingRole() {
    editingRole.value = true;
    nextTick(() => roleInputRef.value?.focus());
}

const readOnlyFields = computed(() => {
    const d = props.hrisData;
    return [
        { key: "manager", label: "You report to", value: d.manager },
        { key: "department", label: "Your team", value: d.department },
        { key: "hire_date", label: "You started on", value: d.hire_date },
    ].filter((f) => f.value);
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
.hris-profile {
    @apply rounded-2xl border border-[#E8E8E8] bg-white p-6 flex flex-col items-center;
}

.inline-edit-input {
    @apply w-full rounded-md border border-valence-grey-300 px-2 py-1 body-small text-gray-600 outline-valence-primary-500;
}
</style>
